import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NavigationExtras, Params, Router } from '@angular/router';
import { catchError, Observable, of, throwError } from 'rxjs';

import { EnvironmentService, ErableHttpService, IdUtils, UaService } from '@ecm/consumer';
import { EcmDispatcherModule, EcmGlobalThisUtil, EnvUtils } from '@ecm/dispatcher';
import { PortfolioFlagsService, UrlHelper as UrlHelperPortfolio } from '@ecm/portfolio';
import { TMSService } from '@ecm/tracker';

import { Link } from '../business/link/link';
import { LinkReference } from '../business/link/link-reference';
import { LinkApplication } from '../business/link/link.application';
import { LinkChannel } from '../business/link/link.channel';
import { LinkRelation } from '../business/link/link.relation';
import { CappingData } from '../business/tracking/cappingData';
import { TmsData } from '../business/tracking/tmsData';
import { TrackingData } from '../business/tracking/trackingData';
import { UrlHelper } from '../utils/url-helper';
import { RouterService } from './router.service';

@Injectable({
    providedIn: 'root',
})
export class LinkService {
    private readonly tmsService = inject(TMSService);
    private readonly router = inject(Router);
    private readonly routerService = inject(RouterService);
    private readonly portfolioFlagsService = inject(PortfolioFlagsService);
    private readonly environmentService = inject(EnvironmentService);
    private readonly uaService = inject(UaService);
    private readonly http = inject(ErableHttpService);

    private readonly CLASS_NAME: string = 'LinkService';

    /**
     * Transaction properties for BCO monitoring
     */
    private readonly pageId = IdUtils.generateRandomId();

    /**
     * Link variable properties
     */
    private readonly WEBAPP: string = 'webapp';
    private readonly WEB: string = 'web';
    private readonly NEC: string = 'nec';
    private readonly INAPP: string = 'inApp';
    private readonly BROWSER: string = 'browser';
    private readonly REFERENCES: string = 'references';

    private readonly BLACK_LIST_URL: string[] = ['edo.votreconseillerorangeenvisio.com', 'edo.devmeet.vjoinlife.com'];

    /**
     * @deprecated Use getLinkSync instead
     */
    public getLink(link: Link, params?: Map<string, string>): Observable<string> {
        return of(this.getLinkSync(link, params) || null);
    }

    /**
     * Synchronous version of getLink as Observable is not mandatory anymore
     */
    public getLinkSync(link: Link, params?: Map<string, string>): string {
        let computedLink = '';
        if (link) {
            const fullLink: Link = JSON.parse(this.setUrlParams(params, JSON.stringify(link)));
            if (Object.hasOwn(fullLink, this.WEBAPP) && this.uaService.isMobile()) {
                if (Object.hasOwn(fullLink.webapp, this.NEC)) {
                    computedLink = this.getNecMobileLink(fullLink.webapp.nec, params);
                } else {
                    computedLink = this.getLinkSync(fullLink.webapp, params);
                }
            } else if (Object.hasOwn(fullLink, this.WEB)) {
                computedLink = this.getLinkSync(fullLink.web, params);
            } else if (Object.hasOwn(fullLink, this.INAPP)) {
                computedLink = fullLink.inApp;
            } else if (Object.hasOwn(fullLink, this.NEC)) {
                computedLink = this.getNecUrl().concat(fullLink.nec);
            } else if (Object.hasOwn(fullLink, this.BROWSER)) {
                computedLink = fullLink.browser;
            } else if (Object.hasOwn(fullLink, this.REFERENCES)) {
                const validLink =
                    this.getValidNativeLink(fullLink.references) ?? this.getValidWebLink(fullLink.references);
                computedLink = this.getLinkSync(validLink, params);
            }
        }
        return computedLink;
    }

    /**
     * Manage to open link for inApp, nec or browser url
     * @param {Link} link
     * @param {TrackingData} trackingData
     * @param {Map<string, string>} [params]
     * @param {NavigationExtras} [navigationExtras] query param object
     * @param {boolean} [openInCurrentTab] Set to `true` if link must be open in current tab (default value is `false`)
     */
    public openLink(
        link: Link,
        trackingData: TrackingData,
        params?: Map<string, string>,
        navigationExtras?: NavigationExtras,
        openInCurrentTab = false,
    ): void {
        if (!link) {
            EcmDispatcherModule.sendConsoleError(this.CLASS_NAME, 'openLink called with null or undefined link');
            return;
        }
        if (Object.hasOwn(link, this.WEBAPP) && this.uaService.isMobile()) {
            return this.handleMobileWebappLink(link.webapp, trackingData, params, navigationExtras, openInCurrentTab);
        }
        if (Object.hasOwn(link, this.WEB)) {
            return this.openLink(link.web, trackingData, params, navigationExtras, openInCurrentTab);
        }
        this.handleLinkType(link, trackingData, params, navigationExtras, openInCurrentTab);
    }

    /**
     * Handle mobile webapp link routing
     * @private
     */
    private handleMobileWebappLink(
        webappLink: Link,
        trackingData: TrackingData,
        params: Map<string, string>,
        navigationExtras: NavigationExtras,
        openInCurrentTab: boolean,
    ): void {
        if (Object.hasOwn(webappLink, this.NEC)) {
            this.openNecMobileLink(webappLink.nec, trackingData, params);
        } else {
            this.openLink(webappLink, trackingData, params, navigationExtras, openInCurrentTab);
        }
    }

    /**
     * Handle link type routing (inApp, nec, browser, references)
     * @private
     */
    private handleLinkType(
        link: Link,
        trackingData: TrackingData,
        params: Map<string, string>,
        navigationExtras: NavigationExtras,
        openInCurrentTab: boolean,
    ): void {
        if (Object.hasOwn(link, this.INAPP)) {
            this.openInAppLink(link.inApp, trackingData, params, navigationExtras);
        } else if (Object.hasOwn(link, this.NEC)) {
            this.openNecLink(link.nec, trackingData, params);
        } else if (Object.hasOwn(link, this.BROWSER)) {
            this.openBrowserLink(link.browser, trackingData, openInCurrentTab, params, navigationExtras);
        } else if (Object.hasOwn(link, this.REFERENCES)) {
            this.handleReferenceLink(link.references, trackingData, params, navigationExtras, openInCurrentTab);
        }
    }

    /**
     * Handle reference link with native and web link fallback
     * @private
     */
    private handleReferenceLink(
        references: LinkReference[],
        trackingData: TrackingData,
        params: Map<string, string>,
        navigationExtras: NavigationExtras,
        openInCurrentTab: boolean,
    ): void {
        const validNativeLink: Link = this.getValidNativeLink(references);
        if (validNativeLink) {
            if (!this.dispatchUrlToNative(this.setUrlParams(params, validNativeLink.inApp), trackingData)) {
                // We are on an unknown device OS, so we use standard openLink
                this.openLink(validNativeLink, trackingData, params, navigationExtras, openInCurrentTab);
            }
        } else {
            // Using default web link
            const validLink: Link = this.getValidWebLink(references);
            this.openLink(validLink, trackingData, params, navigationExtras, openInCurrentTab);
        }
    }

    /**
     * Check if link route to external url
     * @param {Link} link
     * @return {boolean} Link is external
     */
    public isBrowserLink(link: Link): boolean {
        let isBrowser = false;
        if (link) {
            if (Object.hasOwn(link, 'webapp') && this.uaService.isMobile()) {
                isBrowser = Object.hasOwn(link.webapp, 'browser');
            } else if (Object.hasOwn(link, 'web')) {
                isBrowser = Object.hasOwn(link.web, 'browser');
            } else if (Object.hasOwn(link, 'browser')) {
                isBrowser = true;
            } else if (Object.hasOwn(link, 'references')) {
                isBrowser = this.isBrowserLink(this.getValidWebLink(link.references));
            }
        }
        return isBrowser;
    }

    public dispatchUrlToNative(url: string, trackingData?: TrackingData): boolean {
        if (!url.includes('source=') && !this.hasBlackListUrl(url) && UrlHelper.isOrangeOrSoshDomain(url)) {
            const queryParams = { source: this.buildTrackingSourceQueryParams(trackingData || ({} as TrackingData)) };
            url = this.setUrlQueryParams(queryParams, url);
        }

        const message = `Sending post message to parent container with url: ${url}`;
        EcmDispatcherModule.sendConsoleDebug(this.CLASS_NAME, message);

        if (typeof (globalThis as any)?.webkit !== 'undefined') {
            (globalThis as any).webkit?.messageHandlers?.['doInAppNavigation']?.postMessage({ inApp: url });
            return true;
        } else if (typeof (globalThis as any)?.NativeApplication !== 'undefined') {
            (globalThis as any).NativeApplication?.doInAppNavigation(url);
            return true;
        }

        return false;
    }

    /**
     * Check if url is in black list
     * @param url
     * @private
     */
    private hasBlackListUrl(url: string) {
        return this.BLACK_LIST_URL.some((item) => url.includes(item));
    }

    /**
     * Set Object Query params in url
     * @param {<string, string>} queryParams
     * @param {string} browserUrl
     * @return {string}
     */
    public setUrlQueryParams(queryParams: Params, browserUrl: string): string {
        if (!queryParams) {
            return browserUrl;
        }
        /**
         * Take the current url and add the query params
         * if the url already contains query params, we add new params with '&' separator
         * else we add new params with '?' separator
         * if there is an anchor (#) in the url, we add the query params before the anchor
         */
        let url = browserUrl;
        const anchorIndex = url.indexOf('#');
        let anchor = '';
        if (anchorIndex !== -1) {
            anchor = url.substring(anchorIndex);
            url = url.substring(0, anchorIndex);
        }
        Object.keys(queryParams).forEach((key: string) => {
            const separator = url.includes('?') ? '&' : '?';
            const value = queryParams[key];
            url += `${separator}${key}=${value}`;
        });
        url += anchor;
        return url;
    }

    /**
     * Open and track nec link
     * @param {string} necLink Nec uri
     * @param {TrackingData} trackingData
     * @param {Map<string, string>} params
     */
    private openNecLink(necLink: string, trackingData: TrackingData, params: Map<string, string>): void {
        this.redirectToNec(this.getNecUrl(), necLink, trackingData, params);
    }

    /**
     * Open and track nec link on mobile device
     * @param {string} necLink Nec uri
     * @param {TrackingData} trackingData
     * @param {Map<string, string>} [params]
     */
    private openNecMobileLink(necLink: string, trackingData: TrackingData, params?: Map<string, string>): void {
        this.redirectToNec(this.getNecMobileUrl(), necLink, trackingData, params);
    }

    /**
     * Generate link for href property
     * @param {string} necLink link
     * @param {Map<string, string>} [params]
     * @return {string}
     */
    private getNecMobileLink(necLink: string, params?: Map<string, string>): string {
        return this.getNecMobileUrl() + this.setUrlParams(params, necLink);
    }

    /**
     * Set Map params in url
     * @param {Map<string, string>} params
     * @param {string} link
     * @return {string}
     */
    private setUrlParams(params: Map<string, string>, link: string): string {
        if (params != null && params.size > 0) {
            params.forEach((value: string, key: string) => {
                link = link.split(key).join(value);
            });
        }
        return link;
    }

    /**
     * Open and track external link in new tab
     * @param {string} browserLink External uri
     * @param {TrackingData} trackingData
     * @param {boolean} openInCurrentTab open link in current tab
     * @param {Map<string, string>} [params]
     * @param {NavigationExtras} [navigationExtras] query param object*/
    private openBrowserLink(
        browserLink: string,
        trackingData: TrackingData,
        openInCurrentTab: boolean,
        params?: Map<string, string>,
        navigationExtras?: NavigationExtras,
    ): void {
        navigationExtras = navigationExtras || {};
        navigationExtras.queryParams = navigationExtras.queryParams || {};
        if (
            !('source' in navigationExtras.queryParams) &&
            !browserLink.includes('source=') &&
            !this.hasBlackListUrl(browserLink) &&
            UrlHelper.isOrangeOrSoshDomain(browserLink)
        ) {
            navigationExtras.queryParams['source'] = this.buildTrackingSourceQueryParams(trackingData);
        }

        browserLink = this.setUrlQueryParams(navigationExtras.queryParams, browserLink);

        this.trackLink(trackingData, this.getTrackingUrl(browserLink, params));
        if (openInCurrentTab) {
            this.routerService.redirectToExternalUrl(this.setUrlParams(params, browserLink));
        } else {
            this.routerService.redirectToExternalTabUrl(this.setUrlParams(params, browserLink));
        }
    }

    /**
     * Open and track internal link
     * @param {string} inAppLink Internal uri
     * @param {TrackingData} trackingData
     * @param {Map<string, string>} [params]
     * @param {NavigationExtras} [queryParams] query param object
     */
    private openInAppLink(
        inAppLink: string,
        trackingData: TrackingData,
        params?: Map<string, string>,
        queryParams?: NavigationExtras,
    ): void {
        const routeWithParams: string = this.setUrlParams(params, inAppLink.split('{{cid}}').join('idContrat'));
        const routeToNavigate: string = this.setUrlParams(params, inAppLink);

        if (queryParams) {
            const trackedUrl: string = this.router.createUrlTree([routeWithParams], queryParams).toString();
            this.trackLink(trackingData, this.getTrackingUrl(trackedUrl));
            this.routerService.navigate([routeToNavigate], queryParams);
        } else {
            const trackedUrl: string = this.router.createUrlTree([routeWithParams]).toString();
            this.trackLink(trackingData, this.getTrackingUrl(trackedUrl));
            this.routerService.navigateByUrl(routeToNavigate);
        }
    }

    /**
     * Redirect to nec and track click
     * @param {string} necUrl Nec host
     * @param {string} necLink Nec uri
     * @param {TrackingData} trackingData
     * @param {Map<string, string>} params
     */
    private redirectToNec(
        necUrl: string,
        necLink: string,
        trackingData: TrackingData,
        params: Map<string, string>,
    ): void {
        const computedNecUrl: string = necUrl.concat(necLink);
        this.trackLink(trackingData, this.getTrackingUrl(computedNecUrl, params));
        this.routerService.redirectToExternalUrl(this.setUrlParams(params, computedNecUrl));
    }

    /**
     * Redirect to nec and track click
     * @param {string} url
     * @param {Map<string, string>} [params]
     */
    private getTrackingUrl(url: string, params?: Map<string, string>): string {
        return this.setUrlParams(
            params,
            url.split('{{cid}}').join('idContrat').split(UrlHelperPortfolio.matchCid(url)?.[1]).join('idContrat'),
        );
    }

    /**
     * Track click for TMS or capping
     * @param {TrackingData} trackingData
     * @param {string} targetUrl
     */
    public trackLink(trackingData: TrackingData, targetUrl: string): void {
        if (trackingData.tmsTracking) {
            this.trackTmsLink(trackingData.tmsTracking, targetUrl);
        }
        if (trackingData.cappingTracking) {
            this.trackCappingLink(trackingData.cappingTracking, trackingData.featureName, trackingData.functionality);
        }
    }

    /**
     *  Track tms link
     * @param {TmsData} tmsData tms Data
     * @param {string} targetUrl
     */
    private trackTmsLink(tmsData: TmsData, targetUrl: string): void {
        const linkData: any = {
            track_page: tmsData.trackedPage,
            track_zone: tmsData.trackingArea,
            track_nom: tmsData.trackingName,
            track_cible: targetUrl,
            track_type_evt: 'click',
        };
        if (tmsData.additionalProperties) {
            tmsData.additionalProperties.forEach((value, key) => {
                linkData[key] = value;
            });
        }
        if (tmsData.trackingDisplayUniverse) {
            linkData.univers_affichage = tmsData.trackingDisplayUniverse;
        }
        if (tmsData.trackingPageTitle) {
            linkData.titre_page = tmsData.trackingPageTitle;
        }
        this.tmsService.trackLink(linkData);
    }

    /**
     * Call tracking service with capping data
     * @param {CappingData} cappingData
     * @param {string} featureName
     * @param {string} functionality
     */
    private trackCappingLink(cappingData: CappingData, featureName: string, functionality: string): void {
        this.requestPostCapping(cappingData, featureName, functionality).subscribe({
            next: () => {
                EcmDispatcherModule.sendConsoleInfo(this.CLASS_NAME, 'capping success');
            },
            error: () => {
                EcmDispatcherModule.sendConsoleError(this.CLASS_NAME, 'error on capping request');
            },
        });
    }

    private getSortedNativeLinks(referencesLinks: LinkReference[]): LinkReference[] {
        return this.getSortedLinks(referencesLinks, 'linkNative');
    }

    private getSortedWebLinks(referencesLinks: LinkReference[]): LinkReference[] {
        return this.getSortedLinks(referencesLinks, 'linkWeb');
    }

    private getSortedLinks(referencesLinks: LinkReference[], key: 'linkNative' | 'linkWeb'): LinkReference[] {
        const filteredLinks = referencesLinks.filter((reference) => key in reference);
        return filteredLinks.sort(
            (left, right): number =>
                Number(right?.criteria?.channel === LinkChannel.webview) -
                Number(left?.criteria?.channel === LinkChannel.webview),
        );
    }

    public getValidNativeLink(referencesLinks: LinkReference[]): Link | null {
        if (!EnvUtils.isWebview()) {
            return null;
        }
        const referenceLink = this.getSortedNativeLinks(referencesLinks).find(
            (link) => link?.criteria?.channel === LinkChannel.webview,
        );
        return referenceLink ? referenceLink.linkNative : null;
    }

    public getValidWebLink(referencesLinks: LinkReference[]): Link {
        const sortedWebLinks = this.getSortedWebLinks(referencesLinks);
        let referenceLink: LinkReference = null;
        for (const link of sortedWebLinks) {
            if (!Object.hasOwn(link, 'criteria')) {
                referenceLink = link;
                break;
            } else {
                const validChannel: boolean =
                    !Object.hasOwn(link.criteria, 'channel') || this.isValidChannel(link.criteria.channel);
                const validRelation: boolean =
                    !Object.hasOwn(link.criteria, 'relation') || this.isValidRelation(link.criteria.relation);
                const validApplication: boolean =
                    !Object.hasOwn(link.criteria, 'application') || this.isValidApplication(link.criteria.application);

                if (validChannel && validRelation && validApplication) {
                    referenceLink = link;
                    break;
                }
            }
        }
        return referenceLink !== null ? referenceLink.linkWeb : null;
    }

    /**
     * Check if channel is compatible with current environment or user agent
     * @param {LinkChannel} channel Link channel
     */
    private isValidChannel(channel: LinkChannel): boolean {
        return (
            channel === LinkChannel.default ||
            (EnvUtils.isWebview() && channel === LinkChannel.webview) ||
            (this.uaService.isMobile() && channel === LinkChannel.webapp) ||
            (channel === LinkChannel.web && !EnvUtils.isWebview())
        );
    }

    /**
     * Check if application is compatible with current brand context
     * @param {LinkApplication} application Link application
     */
    private isValidApplication(application: LinkApplication): boolean {
        return (
            application === LinkApplication.default ||
            (this.portfolioFlagsService.isSosh() && application === LinkApplication.sosh) ||
            (!this.portfolioFlagsService.isSosh() && application === LinkApplication.orange)
        );
    }

    /**
     * Check if relation is compatible with current environment
     * @param {LinkRelation} relation Link relation
     */
    private isValidRelation(relation: LinkRelation): boolean {
        return (
            relation === LinkRelation.default ||
            (EnvUtils.isEclair() && relation === LinkRelation.crm) ||
            (!EnvUtils.isEclair() && relation === LinkRelation.client)
        );
    }

    /**
     * Gets NEC desktop related URL from i18n
     * @returns {string} The requested url
     */
    private getNecUrl(): string {
        return this.environmentService.instant('NEC.NEC_DESKTOP_URL');
    }

    /**
     * Gets NEC mobile related URL from i18n
     * @returns {string} The requested url
     */
    private getNecMobileUrl(): string {
        return this.environmentService.instant('NEC.NEC_MOBILE_URL');
    }

    /**
     * XHR nested requests to capping endpoint
     * @param {CappingData} cappingData
     * @param {string} featureName
     * @param {string} functionality
     * @returns {Observable<any>}
     */
    private requestPostCapping(cappingData: CappingData, featureName: string, functionality: string): Observable<any> {
        const env = this.environmentService.instant();

        if (env.API.TRACKER.CAPPING_URL !== false) {
            return this.http
                .post(env.API.TRACKER.CAPPING_URL, [cappingData], {
                    feature: featureName,
                    functionality: functionality,
                    pageId: this.pageId,
                    partner: 'ZCE',
                    service: 'capping',
                })
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        return throwError(() => error.error.error);
                    }),
                );
        } else {
            return throwError(() => 'capping unavailable');
        }
    }

    /**
     * Constructs the `source` string.
     * The `source` string is used as a query parameter in URLs.
     *
     * @param {TrackingData} trackingData
     * @returns {string}
     */
    private buildTrackingSourceQueryParams(trackingData: TrackingData): string {
        let source = 'ec';
        // if browser url contains source, we do not override it ECQ-48872
        const sourceMatch = /source=([^&]+)/.exec(EcmGlobalThisUtil.getLocation().href);

        if (sourceMatch?.[1]) {
            return sourceMatch[1];
        }
        if (trackingData?.tmsTracking?.trackedPage) {
            const formattedTrackingPage = this.normalizeString(trackingData.tmsTracking.trackedPage);
            source = `${source}_${formattedTrackingPage}`;
            if (trackingData?.tmsTracking?.trackingName) {
                const formattedTrackingName = this.normalizeString(trackingData.tmsTracking.trackingName);
                source += `-${formattedTrackingName}`;
            }
        }
        return source;
    }

    private normalizeString(input: string): string {
        const decodedInput = decodeURIComponent(input); // Decode URI components if necessary
        return decodedInput
            .normalize('NFD') // Decomposes characters with accents
            .replace(/[\u0300-\u036f]/g, '') // Removes accents
            .replace(/\W/g, '') // Removes non-alphanumeric characters except underscores
            .toLowerCase(); // Converts to lowercase
    }
}
